import React, { useEffect, useState } from "react";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { RiEyeCloseLine } from "react-icons/ri";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { FaLock, FaUnlock, FaShieldAlt, FaKey, FaUserLock, FaUserShield } from "react-icons/fa";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { keyframes } from "@emotion/react";
import { toastState } from "atoms/toastState";
import { useSetRecoilState } from "recoil";
import useAxios from "axios-hooks";
import { configureAxios } from "utils/api";

import DefaultAuth from "layouts/auth/Default";
import LOGO from "../../../assets/img/cmsLogo.svg";

// Define animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const ResetPasswordPage = () => {
  // Chakra color mode
  //const textColor = useColorModeValue("navy.700", "white");
  const textColor = "white";
  //const textColorSecondary = "gray.400";
  const textColorSecondary = "white";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const [isLoading, setIsLoading] = useState(false);
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );

  const [show, setShow] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState("");
  const setToast = useSetRecoilState(toastState);
  const navigate = useNavigate();
  const location = useLocation();

  const schema = yup.object().shape({
    NewPassword: yup.string().required("password is required"),
    ConfirmNewPassword: yup.string().required("password is required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const [{ data, loading: resetPasswordLoading, error }, resetPassword] =
    useAxios(
      { method: "POST", url: `/api/auth/resetpassword` },
      { manual: true }
    );

  const [
    {
      data: encodedStringValid,
      loading: encodedStringValidLoading,
      error: encodedStringValidError,
    },
    verifyResetPasswordString,
  ] = useAxios(
    { method: "POST", url: `/api/auth/verifyresetpasswordstring` },
    { manual: true }
  );

  const handleClick = () => setShow(!show);

  const verifyEncodedString = async (resetToken) => {
    try {
      let params = {};
      params["encodedString"] =
        location?.state?.encodedString !== undefined
          ? location?.state?.encodedString
          : resetToken;
      const response = await verifyResetPasswordString({ params });
      if (response?.data?.redirect) {
        navigate(response?.data?.redirect);
      }
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "UnknownError",
          status: "error",
        });
        navigate("/auth/signIn");
      }
    }
  };
  const submitHandler = async () => {
    setIsLoading(true);
    const formValues = getValues();
    const NewPassword = formValues.NewPassword;
    const ConfirmNewPassword = formValues.ConfirmNewPassword;
    if (NewPassword === ConfirmNewPassword) {
      try {
        let reqData = {};
        // reqData["email"] = location?.state?.email;
        reqData["newPassword"] = NewPassword;
        reqData["encodedString"] =
          location?.state?.encodedString !== undefined
            ? location?.state?.encodedString
            : resetPasswordToken;
        const response = await resetPassword({ data: reqData });
        navigate("/auth/signIn");
        setToast({
          isOpen: true,
          title: "Password reset successfully",
          status: "success",
        });
        setIsLoading(false);
      } catch (error) {
        if (error?.code !== "ERR_CANCELED") {
          setToast({
            isOpen: true,
            title: "UnknownError",
            status: "error",
          });
          navigate("/auth/signIn");
          setIsLoading(false);
        }
      }
    } else {
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Passwords don't match",
          status: "error",
        });
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    configureAxios(null);
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("resetPasswordToken");
    setResetPasswordToken(resetToken);
    verifyEncodedString(resetToken); // Pass resetToken directly to the function
  }, [location.search]);

  return (
    <DefaultAuth>
      {/* Split background - dark on left, white on right */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width={{ base: "100%", md: "50%" }}
        height="100%"
        bg="#151c24"
        zIndex="-1"
      />
      <Box
        position="absolute"
        top="0"
        right="0"
        width={{ base: "0%", md: "50%" }}
        height="100%"
        bg="white"
        zIndex="-1"
        display={{ base: "none", md: "block" }}
      />

      {/* Centered glassmorphic card for reset password form */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="rgba(21, 28, 36, 0.75)"
        borderRadius="2xl"
        boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.18)"
        backdropFilter="blur(18px)"
        border="1.5px solid rgba(245, 125, 54, 0.15)"
        w={["90%", "85%", "80%", "80%"]}
        maxW="450px"
        p={8}
        position="absolute"
        left={{ base: "50%", md: "25%" }}
        transform="translateX(-50%)"
        zIndex={1}
        _before={{
          content: '""',
          position: "absolute",
          top: "-2px",
          left: "-2px",
          right: "-2px",
          bottom: "-2px",
          borderRadius: "2xl",
          border: "2px solid #f57d36",
          opacity: 0.1,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        <Flex direction="column" align="center" justify="center" w="100%">
          <Image 
            src={LOGO} 
            alt="Urzza Logo" 
            width="auto"
            height="80px"
            mb={6} 
            filter="drop-shadow(0 2px 12px rgba(245, 125, 54, 0.5))" 
            objectFit="contain"
            preserveAspectRatio="xMidYMid meet"
          />
          <Heading color={textColor} fontSize="2xl" mb={2} fontWeight="bold" letterSpacing={1.2}>
            Reset Password
          </Heading>
          <Text color="#f57d36" fontSize="lg" mb={1} fontWeight="semibold" letterSpacing={0.6}>
            Create New Password
          </Text>
          <Text color="gray.300" fontSize="md" mb={8} textAlign="center">
            Enter your new password to secure your account
          </Text>

          <form onSubmit={handleSubmit(submitHandler)} style={{ width: "100%" }}>
            <Controller
              name="NewPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <FormControl isInvalid={errors.NewPassword} mb={6}>
                  <FormLabel color={textColor} fontWeight="semibold">
                    New Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      {...field}
                      type={show ? "text" : "password"}
                      placeholder="Enter new password"
                      bg="rgba(255,255,255,0.13)"
                      border="1.5px solid rgba(245, 125, 54, 0.22)"
                      color={textColor}
                      fontSize="lg"
                      py={6}
                      px={5}
                      borderRadius="xl"
                      _focus={{
                        borderColor: "#f57d36",
                        boxShadow: `0 0 0 2px #f57d36`,
                        bg: "rgba(255,255,255,0.22)",
                      }}
                      transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                    />
                    <InputRightElement height="full" display="flex" alignItems="center" pr={2}>
                      <Icon
                        color="#f57d36"
                        _hover={{ cursor: "pointer", color: "#ff914d" }}
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                        boxSize={6}
                        transition="all 0.2s"
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.NewPassword && errors.NewPassword.message}</FormErrorMessage>
                </FormControl>
              )}
            />
            
            <Controller
              name="ConfirmNewPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <FormControl isInvalid={errors.ConfirmNewPassword} mb={6}>
                  <FormLabel color={textColor} fontWeight="semibold">
                    Confirm New Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      {...field}
                      type={show ? "text" : "password"}
                      placeholder="Confirm your new password"
                      bg="rgba(255,255,255,0.13)"
                      border="1.5px solid rgba(245, 125, 54, 0.22)"
                      color={textColor}
                      fontSize="lg"
                      py={6}
                      px={5}
                      borderRadius="xl"
                      _focus={{
                        borderColor: "#f57d36",
                        boxShadow: `0 0 0 2px #f57d36`,
                        bg: "rgba(255,255,255,0.22)",
                      }}
                      transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                    />
                    <InputRightElement height="full" display="flex" alignItems="center" pr={2}>
                      <Icon
                        color="#f57d36"
                        _hover={{ cursor: "pointer", color: "#ff914d" }}
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                        boxSize={6}
                        transition="all 0.2s"
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {errors.ConfirmNewPassword && errors.ConfirmNewPassword.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />

            <Flex justify="space-between" align="center" mt={6} mb={8} w="100%">
              <Button
                type="submit"
                w="60%"
                py={6}
                fontSize="lg"
                fontWeight="bold"
                color="white"
                bgGradient="linear(90deg, #f57d36 60%, #ff914d 100%)"
                boxShadow="0 2px 16px 0 rgba(245, 125, 54, 0.18)"
                borderRadius="xl"
                _hover={{
                  bg: "#f57d36",
                  boxShadow: "0 4px 24px 0 rgba(245, 125, 54, 0.29)",
                  transform: "scale(1.03)",
                }}
                _active={{
                  bg: "#f57d36",
                  boxShadow: "0 2px 12px 0 rgba(245, 125, 54, 0.16)",
                }}
                transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                isLoading={isLoading}
                loadingText="Updating..."
              >
                Update Password
              </Button>
              <Text
                as="button"
                color="#f57d36"
                fontSize="sm"
                fontWeight="medium"
                bg="none"
                border="none"
                cursor="pointer"
                _hover={{ textDecoration: "underline", color: "#ff914d" }}
                onClick={() => navigate("/auth/signIn")}
                transition="color 0.15s"
                type="button"
              >
                Back to Sign In
              </Text>
            </Flex>
          </form>
        </Flex>
      </Flex>

      {/* Right side illustration - hidden on mobile */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="50%"
        height="100%"
        zIndex={0}
        display={{ base: "none", md: "none", lg: "flex" }}
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <Box
          width="80%"
          maxW="600px"
          height="auto"
          bg="white"
          borderRadius="2xl"
          boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.08)"
          border="1.5px solid rgba(245, 125, 54, 0.15)"
          p={8}
          textAlign="center"
        >
          <Box 
            position="relative"
            h="280px"
            mb={5}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* Main lock icon with pulse animation */}
            <Box
              position="relative"
              animation={`${pulse} 2s infinite ease-in-out`}
              zIndex={2}
            >
              <Icon 
                as={FaLock} 
                boxSize={36} 
                color="#f57d36"
                filter="drop-shadow(0 8px 20px rgba(245, 125, 54, 0.3))"
              />
            </Box>
            
            {/* Rotating key in background */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              animation={`${rotate} 20s linear infinite`}
              opacity={0.15}
              zIndex={1}
            >
              <Icon 
                as={FaKey} 
                boxSize={100} 
                color="#f57d36"
              />
            </Box>
            
            {/* Circle of icons around the main icon */}
            <Box position="absolute" top="50%" left="50%" w="240px" h="240px" transform="translate(-50%, -50%)">
              {/* Unlock icon */}
              <Box position="absolute" top="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out`}>
                <Icon as={FaUnlock} boxSize={10} color="#3182CE" />
              </Box>
              
              {/* Shield icon */}
              <Box position="absolute" top="25%" right="0" animation={`${float} 3s infinite ease-in-out 0.5s`}>
                <Icon as={FaShieldAlt} boxSize={10} color="#38A169" />
              </Box>
              
              {/* Key icon */}
              <Box position="absolute" bottom="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out 1s`}>
                <Icon as={FaKey} boxSize={10} color="#805AD5" />
              </Box>
              
              {/* User Lock icon */}
              <Box position="absolute" top="25%" left="0" animation={`${float} 3s infinite ease-in-out 1.5s`}>
                <Icon as={FaUserLock} boxSize={10} color="#DD6B20" />
              </Box>
              
              {/* User Shield icon */}
              <Box position="absolute" bottom="25%" right="0" animation={`${float} 3s infinite ease-in-out 2s`}>
                <Icon as={FaUserShield} boxSize={10} color="#E53E3E" />
              </Box>
              
              {/* Lock icon */}
              <Box position="absolute" bottom="25%" left="0" animation={`${float} 3s infinite ease-in-out 2.5s`}>
                <Icon as={FaLock} boxSize={10} color="#D53F8C" />
              </Box>
            </Box>
          </Box>
          <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
            Secure Your Account
          </Heading>
          <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
            Create a strong password to keep your account protected
          </Text>
        </Box>
      </Box>
    </DefaultAuth>
  );
};

export default ResetPasswordPage;
